import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Heart,
  Phone,
  MapPin,
  MessageCircle,
  Sparkles,
  Camera,
  ArrowRight,
} from "lucide-react";
import { MESSENGER_URL, BOOKING_URL } from "@/constants/content";
import { useSiteContent } from "@/hooks/useSiteContent";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

function PhotoSlot({ src, alt, label, className = "" }) {
  if (src) {
    return (
      <div className={`overflow-hidden rounded-[1.75rem] border border-[#EAE5D9] ${className}`}>
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="w-full h-full object-cover"
        />
      </div>
    );
  }
  return (
    <div
      className={`rounded-[1.75rem] border-2 border-dashed border-[#EAE5D9] bg-[#F6F3EB]/50 flex flex-col items-center justify-center p-6 text-center text-[#758C88] ${className}`}
    >
      <Camera className="h-8 w-8 mb-2 text-[#A88A2B]/60" />
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#A88A2B]">
        {label}
      </div>
      <div className="text-xs mt-1 italic">Kép hamarosan</div>
    </div>
  );
}

export default function Rolunk() {
  const { content } = useSiteContent();
  const studio = content.studio || {};

  return (
    <div data-testid="rolunk-page" className="text-[#1A2825]">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-10">
        <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#A88A2B]">
            <Heart className="h-3.5 w-3.5" /> Rólunk
          </span>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2825] mt-4 leading-[1.05]">
            Ismerj meg <em className="not-italic text-[#2A9D8F]">minket</em>
          </h1>
          <p className="mt-4 text-xl italic font-light text-[#A88A2B] leading-relaxed">
            „A harmónia belül kezdődik, és kívül látszik."
          </p>
          <p className="mt-6 text-lg text-[#3A4D4A] font-light leading-relaxed">
            Gondoltál már bele, hogy hányszor mondjuk egy sűrű nap végén:
            <em> „Kifutok az időből, csupa logisztika az életem"</em>?
            Édesanyaként, társként és elfoglalt nőként mi is pontosan ismerjük ezt a mindennapi
            hajtást, a folyamatos pörgést és a problémamegoldást. Csapatunkkal hisszük, hogy a
            rohanó világban minden nőnek, férfinak és gyermeknek szüksége van egy szigetre, ahol
            csak Ő van.
          </p>
          <p className="mt-4 text-lg text-[#3A4D4A] font-light leading-relaxed">
            A stúdiónk azért született meg, hogy ezt a szigetet biztosítsuk neked, ahol
            <strong className="font-medium"> a harmónia belül kezdődik és kívül látszik</strong>.
            Engedd meg, hogy bemutatkozzunk!
          </p>
        </motion.div>
      </section>

      {/* Rita section */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-14 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-5">
            <PhotoSlot
              src={studio.photoRita}
              alt="Takácsné Kiss Rita"
              label="Rita fotó"
              className="h-96"
            />
          </div>
          <div className="md:col-span-7">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#2A9D8F]">
              Alapító
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-tight">
              Rita
            </h2>
            <p className="mt-2 italic text-[#A88A2B]">„A harmónia elkötelezett híve"</p>
            <div className="mt-5 space-y-4 text-[#3A4D4A] font-light leading-relaxed">
              <p>
                Sziasztok! <strong className="font-medium">Takácsné Kiss Rita</strong> vagyok, két
                gyermekes anya, társ, elfoglalt nő. Mindig is nagyon szerettem emberekkel
                foglalkozni, a problémájukat felgöngyölíteni, és a masszázs erejével segíteni
                nekik a feltöltődésben.
              </p>
              <p>
                A családom szerint arany kezem van – és egy régi, dédelgetett álmom vált valóra
                azzal, hogy megnyitottam ezt a masszázs stúdiót. Most jött el az idő. Számomra a
                masszázs nemcsak fizikai felfrissülés, hanem a lélek lecsendesítése is. Arra
                törekszem, hogy a hozzám érkezők a kezelés végén teljesen újrahangolva, könnyebb
                léptekkel távozzanak.
              </p>
            </div>
            <a
              href="tel:+36704324269"
              className="mt-6 inline-flex items-center gap-2 text-[#1C6B61] hover:text-[#2A9D8F] font-medium"
            >
              <Phone className="h-4 w-4" /> +36 70 432-4269
            </a>
          </div>
        </div>
      </section>

      {/* Annamari section (reversed layout) */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-14 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7 md:order-1 order-2">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#A88A2B]">
              Szakképzett gyógymasszőr
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-tight">
              Annamari
            </h2>
            <p className="mt-2 italic text-[#A88A2B]">„A szakképzett gyógymasszőr"</p>
            <div className="mt-5 space-y-4 text-[#3A4D4A] font-light leading-relaxed">
              <p>
                Sziasztok! <strong className="font-medium">Zólyominé Felner Annamáriának</strong> hívnak,
                de szólítsatok nyugodtan Annamarinak. Négygyermekes anyukaként az évek során
                megtanultam zsonglőrködni az idővel – most pedig végre eljött az én időm is:
                megnyitottuk a TestLélekHarmónia masszázs stúdiót!
              </p>
              <p>
                Szakképzett gyógymasszőrként hiszek abban, hogy a{" "}
                <strong className="font-medium">test és a lélek csak együtt kerülhet egyensúlyba</strong>.
                Vidám, barátságos természetemmel igyekszem minden vendégemnek egy kis megnyugvást,
                törődést és feltöltődést adni. Szeretettel várlak, ha egy kis megállóra vágysz a
                rohanó világban!
              </p>
            </div>
            <a
              href="tel:+36307153708"
              className="mt-6 inline-flex items-center gap-2 text-[#A88A2B] hover:text-[#D4AF37] font-medium"
            >
              <Phone className="h-4 w-4" /> +36 30 715-3708
            </a>
          </div>
          <div className="md:col-span-5 md:order-2 order-1">
            <PhotoSlot
              src={studio.photoAnnamari}
              alt="Zólyominé Felner Annamária"
              label="Annamari fotó"
              className="h-96"
            />
          </div>
        </div>
      </section>

      {/* Miért érdemes szekció + Stúdió fotók */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-14 md:pb-20">
        <div
          data-testid="rolunk-miert"
          className="bg-[#F6F3EB]/80 rounded-[2.5rem] border border-[#D4AF37]/30 p-8 md:p-14"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-7">
              <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A88A2B]">
                <Sparkles className="h-3.5 w-3.5" /> Miért érdemes hozzánk jönnöd?
              </span>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A2825] mt-3 leading-tight">
                Nem futószalagon érkeznek nálunk a vendégek.
              </h2>
              <div className="mt-5 space-y-4 text-[#3A4D4A] font-light leading-relaxed">
                <p>
                  A TestLélekHarmóniában{" "}
                  <strong className="font-medium">kiemelt figyelmet, személyre szabott kezeléseket
                  és valódi nyugalmat</strong> kapsz — legyen szó egy frissítő talpmasszázzsal
                  egybekötött méregtelenítésről, egy illatos Aroma Flow rituáléról, vagy egy mélyen
                  relaxáló meleg gyógynövényes masszázsról.
                </p>
                <p className="italic text-[#A88A2B] text-lg">
                  Mikor ajándékoztál magadnak utoljára egy kis szelet zavartalan nyugalmat?
                </p>
              </div>
            </div>
            <div className="md:col-span-5 grid grid-cols-2 gap-3">
              <PhotoSlot src={studio.photoStudio1} label="Stúdió belső 1" className="h-44 col-span-2" />
              <PhotoSlot src={studio.photoStudio2} label="Stúdió belső 2" className="h-32" />
              <PhotoSlot src={studio.photoStudio3} label="Stúdió belső 3" className="h-32" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative overflow-hidden rounded-[2rem] bg-[#1A2825] text-white p-8 md:p-14 text-center"
        >
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#2A9D8F]/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-[#D4AF37]/15 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
              <MapPin className="h-3.5 w-3.5" /> Várunk szeretettel Tökölön!
            </span>
            <h2 className="font-serif-display text-3xl md:text-4xl font-light mt-4 leading-snug">
              Címünk: <em className="not-italic text-[#D4AF37]">2316 Tököl, Csépi út 114.</em>
            </h2>
            <p className="mt-4 text-white/75 font-light leading-relaxed">
              Ha most éppen elfáradtan olvasod ezt — foglalj egy kis szelet zavartalan nyugalmat
              magadnak. A többit ránk bízhatod.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="rolunk-messenger-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#2A9D8F] hover:bg-[#22857a] text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all shadow-[0_10px_24px_rgba(42,157,143,0.35)] hover:-translate-y-0.5"
              >
                <MessageCircle className="h-4 w-4" /> Foglalok időpontot
              </a>
              <Link
                to="/masszazs"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white px-7 py-3.5 rounded-full font-medium tracking-wide transition-all"
              >
                Kezelések áttekintése <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
